/**
 * The draft as a list of cards: a phone (below 768 px) and the 400 px Outlook pane,
 * where the table does not fit. The same cell renderers as the table, stacked: status
 * and source on line 1, the customer's words on line 2, the proposed item on line 3,
 * the figures on line 4 ("Qty 6 EA · Price 412.50 GBP · Lead 12 days"), the row button,
 * the row menu and the chevron on line 5. The details sit in a Collapsible under the card, open when
 * "Show details" is on or the chevron was pressed.
 *
 * The three groups keep their headings; "Nothing to quote" is folded but stays in the
 * markup, so a test or a search finds its rows. Every cell wraps: nothing scrolls sideways.
 */

import { ChevronDown, Icon } from "@norican/icons";
import { Button, Card, cn, Collapsible, Typography } from "@norican/react";
import { type KeyboardEvent, useEffect, useState } from "react";

import {
  AskedCell,
  type DraftCellContext,
  DraftLineExtras,
  hasUndecidedMatched,
  LeadCell,
  PriceCell,
  ProposedCell,
  QuantityCell,
  RowActionButton,
  sourceKindOf,
  StatusBadge,
  TwinButton,
} from "@/features/case-review/draft-line-cells";
import { type DraftRow } from "@/features/case-review/draft-line-groups";
import { LineMenu } from "@/features/case-review/line-menu";
import { GROUP_ICON, GROUP_TONE, SOURCE_ICON, SOURCE_LABEL } from "@/lib/line-icons";
import { DRAFT_COLUMNS, DRAFT_GROUPS, type DraftGroup, draftGroupSentence } from "@/lib/sales-flow";

/** The left stripe of a card: warning for what needs the person, success for matched. */
const CARD_TONE: Record<DraftGroup, "warning" | "success" | "neutral"> = {
  [DRAFT_GROUPS.needsYou]: "warning",
  [DRAFT_GROUPS.matched]: "success",
  [DRAFT_GROUPS.nothingToQuote]: "neutral",
};

function DraftLineCard({
  row,
  ctx,
  detailsOn,
  focused,
  onOpenDetails,
}: {
  row: DraftRow;
  ctx: DraftCellContext;
  detailsOn: boolean;
  focused: boolean;
  onOpenDetails?: (lineId: string) => void;
}) {
  const { line, word, position } = row;
  // The chevron overrides "Show details" for this visit; the switch resets the override.
  const [toggled, setToggled] = useState<boolean | null>(null);
  useEffect(() => {
    setToggled(null);
  }, [detailsOn]);
  const open = toggled ?? detailsOn;
  const source = sourceKindOf(line);

  return (
    <li
      className={cn(
        "[&[data-row-highlight]]:bg-accent-subtle min-w-0 rounded-md",
        focused && "bg-bg-subtle -mx-2 px-2",
      )}
      data-draft-line={line.id}
      data-line-word={word}
      // A card takes the focus from the page (a blocker sentence, "Go to the draft") and
      // from the arrow keys; Tab still goes to its buttons.
      tabIndex={-1}
      aria-label={`Line ${position}: ${line.enquiryText}`}
    >
      <Card variant="outline" accent="left" accentTone={CARD_TONE[row.group]} className="min-w-0">
        <Card.Body padding="sm" className="flex min-w-0 flex-col gap-1.5">
          <div className="flex min-w-0 items-center justify-between gap-2">
            {/* The row's marks sit on the <li>, so the badge carries none here. */}
            <StatusBadge row={row} marks={false} />
            <Icon
              icon={SOURCE_ICON[source]}
              size="xs"
              label={SOURCE_LABEL[source]}
              className="text-fg-subtle shrink-0"
            />
          </div>
          <div className="min-w-0 break-words">
            <AskedCell row={row} ctx={ctx} form="card" />
          </div>
          <div className="min-w-0 break-words">
            <ProposedCell row={row} ctx={ctx} />
          </div>
          <div className="text-fg-muted flex min-w-0 flex-wrap items-center gap-x-1 text-xs break-words">
            <span className="inline-flex items-center gap-1">
              <span className="text-fg-subtle">{DRAFT_COLUMNS.qty}</span>
              <QuantityCell row={row} ctx={ctx} />
            </span>
            <span aria-hidden> · </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-fg-subtle">{DRAFT_COLUMNS.price}</span>
              <PriceCell row={row} ctx={ctx} />
            </span>
            <span aria-hidden> · </span>
            <span className="inline-flex items-center gap-1">
              <span className="text-fg-subtle">{DRAFT_COLUMNS.lead}</span>
              <LeadCell row={row} ctx={ctx} form="card" />
            </span>
          </div>
          <Collapsible.Root
            open={open}
            onOpenChange={(next) => {
              setToggled(next);
              if (next) {
                onOpenDetails?.(line.id);
              }
            }}
            className="flex min-w-0 flex-col"
          >
            <div className="flex flex-wrap items-center gap-2">
              <RowActionButton row={row} ctx={ctx} />
              <TwinButton row={row} ctx={ctx} />
              <LineMenu row={row} ctx={ctx} />
              {/* The DS trigger draws its own chevron: the word says what the bar opens
                  (round 5: an icon-only trigger read as an empty box on the phone). */}
              <Collapsible.Trigger
                render={
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    className="ml-auto"
                    aria-label={
                      open ? `Hide details of line ${position}` : `Details of line ${position}`
                    }
                  />
                }
              >
                {open ? "Hide details" : "Details"}
              </Collapsible.Trigger>
            </div>
            <Collapsible.Panel>
              <DraftLineExtras row={row} ctx={ctx} />
            </Collapsible.Panel>
          </Collapsible.Root>
        </Card.Body>
      </Card>
    </li>
  );
}

/** The heading of one group: its icon, its name and its sentence. */
function GroupHeading({
  group,
  members,
  ctx,
}: {
  group: DraftGroup;
  members: readonly DraftRow[];
  ctx: Pick<DraftCellContext, "detail">;
}) {
  return (
    <Typography
      as="span"
      variant="caption"
      className="text-fg inline-flex min-w-0 flex-wrap items-center gap-1.5"
    >
      <Icon icon={GROUP_ICON[group]} size="xs" tone={GROUP_TONE[group]} className="shrink-0" />
      <span className="font-semibold">
        {group} ({members.length})
      </span>
      <span className="text-fg-muted">
        {draftGroupSentence(group, hasUndecidedMatched(ctx, members))}
      </span>
    </Typography>
  );
}

/**
 * The card an arrow key moves from: the card that holds the focus itself, or the card of
 * a plain button in it. Null for a field (the arrow keys move its caret), for a button
 * that opens a menu (Arrow down opens it) and for anything outside a card.
 */
export function cardOfArrowKey(target: Element): HTMLElement | null {
  const card = target.closest<HTMLElement>("li[data-draft-line]");
  if (!card) {
    return null;
  }
  if (target === card) {
    return card;
  }
  const plainButton = target.tagName === "BUTTON" && !target.hasAttribute("aria-haspopup");
  return plainButton ? card : null;
}

export interface DraftListProps {
  rows: readonly DraftRow[];
  ctx: DraftCellContext;
  /** "Show details": every card open. A chevron click overrides it for that card. */
  detailsOn: boolean;
  /** The line the primary action points at, focused in the list. */
  focusLineId: string | null;
  onOpenDetails?: (lineId: string) => void;
}

export function DraftList({ rows, ctx, detailsOn, focusLineId, onOpenDetails }: DraftListProps) {
  const [showFolded, setShowFolded] = useState(false);
  const groups = [DRAFT_GROUPS.needsYou, DRAFT_GROUPS.matched, DRAFT_GROUPS.nothingToQuote].map(
    (group) => ({ group, rows: rows.filter((row) => row.group === group) }),
  );
  const card = (row: DraftRow) => (
    <DraftLineCard
      key={row.line.id}
      row={row}
      ctx={ctx}
      detailsOn={detailsOn}
      focused={focusLineId === row.line.id}
      onOpenDetails={onOpenDetails}
    />
  );
  // The arrow keys move from card to card, as they move from row to row in the table:
  // from the card itself, and from a plain button of it (review of the ease round, 13 Oct
  // 2026: after a quantity the focus sat on a button, and the keys had no path on a
  // phone). A field keeps its arrow keys, and so does a button that opens a menu.
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (
      (event.key !== "ArrowDown" && event.key !== "ArrowUp") ||
      event.defaultPrevented ||
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }
    const from = cardOfArrowKey(event.target);
    if (!from) {
      return;
    }
    const cards = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>("li[data-draft-line]"),
    );
    const next = cards[cards.indexOf(from) + (event.key === "ArrowDown" ? 1 : -1)];
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };
  return (
    <ul className="flex min-w-0 flex-col gap-2" aria-label="Draft lines" onKeyDown={onKeyDown}>
      {groups.map(({ group, rows: members }) => {
        if (members.length === 0) {
          return null;
        }
        if (group === DRAFT_GROUPS.nothingToQuote) {
          // Folded, but kept in the markup: a person opens it when they look for a line.
          return (
            <li key={group} className="flex min-w-0 flex-col gap-2" data-draft-group={group}>
              <Collapsible.Root open={showFolded} onOpenChange={setShowFolded}>
                <Collapsible.Trigger
                  className="flex w-full items-center gap-1 text-left"
                  aria-label={showFolded ? `Hide ${group}` : `Show ${group}`}
                >
                  <GroupHeading group={group} members={members} ctx={ctx} />
                  <Icon
                    icon={ChevronDown}
                    size="xs"
                    className={cn(
                      "text-fg-subtle ml-auto transition-transform",
                      showFolded && "rotate-180",
                    )}
                  />
                </Collapsible.Trigger>
                <Collapsible.Panel keepMounted>
                  <ul className="flex min-w-0 flex-col gap-2 pt-2">{members.map(card)}</ul>
                </Collapsible.Panel>
              </Collapsible.Root>
            </li>
          );
        }
        return (
          <li key={group} className="flex min-w-0 flex-col gap-2" data-draft-group={group}>
            <GroupHeading group={group} members={members} ctx={ctx} />
            <ul className="flex min-w-0 flex-col gap-2">{members.map(card)}</ul>
          </li>
        );
      })}
    </ul>
  );
}
